import React, { useState } from 'react';

import {
	BottomCard,
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { Flex } from '@/components/shared/Flex/Flex';
import { Label } from '@/components/shared/Inputs/Label';
import { Select, SelectProps } from '@/components/shared/Inputs/Select';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { FormHelperText } from '@material-ui/core';

import styles from './SongsChipPanel.module.scss';

export interface SongsChipPanelProps {
	initialPre: number;
	initialPost: number;
	currentPreSongCount: number;
	currentPostSongCount: number;
	onSave: (values: {
		totalSongPositions: number;
		postSermonSongPosition: number | null;
	}) => void;
	onClose: () => void;
}

const generateSongItems = (num: number): SelectProps['items'] => {
	const items: SelectProps['items'] = [];
	for (let i = 0; i <= num; i++) {
		items.push({ label: i.toString(), value: i });
	}
	return items;
};

export const SongsChipPanel: React.FC<SongsChipPanelProps> = ({
	initialPre,
	initialPost,
	currentPreSongCount,
	currentPostSongCount,
	onSave,
	onClose,
}) => {
	const [pre, setPre] = useState(initialPre);
	const [post, setPost] = useState(initialPost);

	const fireSaveSongs = useAnalyticsEvent('Generate_Setlist_Save_Songs');

	const showWarning = pre + post < currentPreSongCount + currentPostSongCount;

	return (
		<BottomCard
			title="Songs"
			hasCloseButton={true}
			closeButtonLabel="Cancel"
			onClose={onClose}
		>
			<BottomCardForm>
				<Flex gap={5} flexDirection="row">
					<Flex
						flexDirection="column"
						justifyContent="space-between"
						style={{ flex: 1 }}
					>
						<Label
							withOffset
							tooltipHeading="Pre-sermon songs"
							tooltip="The number of songs you want to play **before** the sermon or main talk."
						>
							Pre-songs
						</Label>
						<Select
							onChange={(newvalue) => {
								if (typeof newvalue === 'number')
									setPre(newvalue);
							}}
							placeholder="Select number of songs"
							value={pre.toString()}
							withBorder
							items={generateSongItems(4)}
							dropdownClassName={styles['select']}
							listClassName={styles['list']}
						/>
					</Flex>
					<Flex
						flexDirection="column"
						justifyContent="space-between"
						style={{ flex: 1 }}
					>
						<Label
							withOffset
							tooltipHeading="Post-sermon songs"
							tooltip="The number of songs you want to play **after** the sermon or main talk. The song immediately after is the **sermon song**."
						>
							Post-songs
						</Label>
						<Select
							onChange={(newvalue) => {
								if (typeof newvalue === 'number')
									setPost(newvalue);
							}}
							placeholder="None"
							value={post.toString()}
							withBorder
							items={generateSongItems(4)}
							dropdownClassName={styles['select']}
							listClassName={styles['list']}
						/>
					</Flex>
				</Flex>
				{showWarning && (
					<FormHelperText className={styles['warning']}>
						Saving will remove some songs from the setlist.
					</FormHelperText>
				)}
			</BottomCardForm>
			<BottomCardAction>
				<AsaphButton
					onClick={() => {
						fireSaveSongs();
						onSave({
							totalSongPositions: pre + post,
							postSermonSongPosition: post > 0 ? pre + 1 : null,
						});
					}}
					fullWidth
					label="Save"
					color="blue"
				/>
			</BottomCardAction>
		</BottomCard>
	);
};
